// Foto di un documento raddrizzata sul telefono, senza librerie: si trovano i quattro bordi del foglio, se ne
// prendono gli angoli e si corregge la prospettiva (omografia). Il cliente vede l'anteprima, può spostare gli
// angoli, poi "Va bene", "Salta" (la foto com'è) o "Rifai"; il file raddrizzato diventa un PDF.
//
// I bordi si cercano sul CONTRASTO (Sobel + Hough), non sulla luminosità: il caso più comune è il foglio bianco
// sulla scrivania chiara, dove una soglia sulla luminosità (Otsu) non separa niente e prende tutta l'inquadratura.
// La soglia di Otsu resta come seconda strada, per le foto dove i bordi non si vedono ma il foglio sì (tavolo
// scuro). Se non siamo sicuri non inventiamo: si propone il rettangolo pieno e lo si dice al cliente.

export const LATO_ANALISI = 320; // l'analisi si fa su una copia piccola: veloce anche sul telefono
export const LATO_MASSIMO = 2000;
export const MARGINE = 0.02; // gli angoli proposti restano dentro la foto (la maniglia resta afferrabile)
export const MARGINE_PIENO = 0.05; // il rettangolo pieno quando i bordi non si trovano

// Soglia di Otsu su un istogramma di grigi (0-255)
export const sogliaOtsu = (grigi) => {
  const ist = new Array(256).fill(0);
  for (let i = 0; i < grigi.length; i += 1) ist[grigi[i]] += 1;
  const totale = grigi.length;
  let somma = 0;
  for (let t = 0; t < 256; t += 1) somma += t * ist[t];
  let sommaB = 0; let pesoB = 0; let migliore = 0; let soglia = 127;
  for (let t = 0; t < 256; t += 1) {
    pesoB += ist[t];
    if (!pesoB) continue;
    const pesoF = totale - pesoB;
    if (!pesoF) break;
    sommaB += t * ist[t];
    const mB = sommaB / pesoB;
    const mF = (somma - sommaB) / pesoF;
    const tra = pesoB * pesoF * (mB - mF) * (mB - mF);
    if (tra > migliore) { migliore = tra; soglia = t; }
  }
  return soglia;
};

export const inGrigi = (rgba, w, h) => {
  const g = new Uint8Array(w * h);
  for (let i = 0; i < w * h; i += 1) g[i] = (rgba[i * 4] * 299 + rgba[i * 4 + 1] * 587 + rgba[i * 4 + 2] * 114) / 1000;
  return g;
};

// Componente connessa (4 vicini) dei pixel accesi che parte da "da"; restituisce gli indici
const componente = (mask, w, h, da, visti) => {
  const pila = [da];
  const pixel = [];
  visti[da] = 1;
  while (pila.length) {
    const p = pila.pop();
    pixel.push(p);
    const x = p % w; const y = (p - x) / w;
    const vicini = [x > 0 ? p - 1 : -1, x < w - 1 ? p + 1 : -1, y > 0 ? p - w : -1, y < h - 1 ? p + w : -1];
    for (const v of vicini) {
      if (v >= 0 && mask[v] && !visti[v]) { visti[v] = 1; pila.push(v); }
    }
  }
  return pixel;
};

// Seconda strada: la zona chiara più grande (soglia di Otsu). Va bene col tavolo scuro, non col tavolo chiaro.
export const trovaAngoliChiaro = (grigi, w, h) => {
  const soglia = sogliaOtsu(grigi);
  const mask = new Uint8Array(w * h);
  for (let i = 0; i < w * h; i += 1) mask[i] = grigi[i] > soglia ? 1 : 0;
  const visti = new Uint8Array(w * h);
  const centro = Math.floor(h / 2) * w + Math.floor(w / 2);
  let migliore = mask[centro] ? componente(mask, w, h, centro, visti) : [];
  if (migliore.length < w * h * 0.15) {
    for (let p = 0; p < w * h; p += 1) {
      if (mask[p] && !visti[p]) {
        const c = componente(mask, w, h, p, visti);
        if (c.length > migliore.length) migliore = c;
      }
    }
  }
  if (migliore.length < w * h * 0.15 || migliore.length > w * h * 0.98) return null;
  let tl = null; let tr = null; let br = null; let bl = null;
  let sMin = Infinity; let sMax = -Infinity; let dMin = Infinity; let dMax = -Infinity;
  for (const p of migliore) {
    const x = p % w; const y = (p - x) / w;
    const s = x + y; const d = x - y;
    if (s < sMin) { sMin = s; tl = [x, y]; }
    if (s > sMax) { sMax = s; br = [x, y]; }
    if (d > dMax) { dMax = d; tr = [x, y]; }
    if (d < dMin) { dMin = d; bl = [x, y]; }
  }
  return [tl, tr, br, bl];
};

// ---------------------------------------------------------------- i bordi (Sobel + Hough)

const GRADI = Math.PI / 180;
const CASELLE = 90; // un grado per casella: rette «in piedi» da -45° a 44°, «sdraiate» da 45° a 134°
const SETTORE = 6; // ogni pixel vota anche 6° intorno al verso del suo gradiente (nessun bordo è perfetto)
const QUANTE_RETTE = 6; // candidate tenute per ogni lato
const PARTE_BORDI = 0.06; // si tiene il 6% dei pixel col contrasto più forte
const CONTRASTO_MINIMO = 1.5; // un bordo è almeno una volta e mezzo il contrasto medio lì intorno
const RUMORE = 3; // sotto questo salto di luce è solo grana della fotocamera
const INTORNO = 12; // raggio della finestra in cui si misura il contrasto medio
const FUORI_CENTRO = 0.06; // un bordo del foglio sta almeno al 6% dal centro
const VOTI_MINIMI = 0.18; // un bordo lungo almeno il 18% del lato della foto
const CAMPIONI = 24; // punti provati lungo ogni lato
const CODA = 0.18; // quanto si guarda oltre gli angoli, in frazione del lato
const APPOGGIO_MINIMO = 0.5; // sotto questo punteggio non siamo sicuri: meglio dirlo

// Media 3×3: toglie la grana, se no il Sobel vede bordi finti dappertutto (foto in penombra)
export const sfoca3 = (g, w, h) => {
  const out = new Uint8Array(w * h);
  for (let y = 0; y < h; y += 1) {
    for (let x = 0; x < w; x += 1) {
      let somma = 0; let quanti = 0;
      for (let dy = -1; dy <= 1; dy += 1) {
        for (let dx = -1; dx <= 1; dx += 1) {
          const yy = y + dy; const xx = x + dx;
          if (yy >= 0 && yy < h && xx >= 0 && xx < w) { somma += g[yy * w + xx]; quanti += 1; }
        }
      }
      out[y * w + x] = somma / quanti;
    }
  }
  return out;
};

// Sobel: di quanto e in che verso cambia la luce in ogni pixel. (gx, gy) è la normale al bordo (dove punta il
// gradiente), `salto` quanto cambia la luce attraversandolo.
const sobel = (g, w, h) => {
  const salto = new Float32Array(w * h);
  const gx = new Float32Array(w * h);
  const gy = new Float32Array(w * h);
  for (let y = 1; y < h - 1; y += 1) {
    for (let x = 1; x < w - 1; x += 1) {
      const i = y * w + x;
      const dx = (g[i - w + 1] + 2 * g[i + 1] + g[i + w + 1]) - (g[i - w - 1] + 2 * g[i - 1] + g[i + w - 1]);
      const dy = (g[i + w - 1] + 2 * g[i + w] + g[i + w + 1]) - (g[i - w - 1] + 2 * g[i - w] + g[i - w + 1]);
      gx[i] = dx; gy[i] = dy;
      salto[i] = Math.hypot(dx, dy) / 4;
    }
  }
  return { salto, gx, gy };
};

// Media del contrasto in una finestra quadrata, con l'immagine integrale (costa uguale per ogni raggio)
const mediaIntorno = (salto, w, h, raggio) => {
  const somme = new Float64Array((w + 1) * (h + 1));
  for (let y = 0; y < h; y += 1) {
    let riga = 0;
    for (let x = 0; x < w; x += 1) {
      riga += salto[y * w + x];
      somme[(y + 1) * (w + 1) + x + 1] = somme[y * (w + 1) + x + 1] + riga;
    }
  }
  const out = new Float32Array(w * h);
  for (let y = 0; y < h; y += 1) {
    const y0 = Math.max(0, y - raggio); const y1 = Math.min(h, y + raggio + 1);
    for (let x = 0; x < w; x += 1) {
      const x0 = Math.max(0, x - raggio); const x1 = Math.min(w, x + raggio + 1);
      const s = somme[y1 * (w + 1) + x1] - somme[y0 * (w + 1) + x1] - somme[y1 * (w + 1) + x0] + somme[y0 * (w + 1) + x0];
      out[y * w + x] = s / ((y1 - y0) * (x1 - x0));
    }
  }
  return out;
};

const campiona = (v, w, h, x, y) => {
  const x0 = Math.max(0, Math.min(w - 2, Math.floor(x))); const y0 = Math.max(0, Math.min(h - 2, Math.floor(y)));
  const fx = x - x0; const fy = y - y0;
  return v[y0 * w + x0] * (1 - fx) * (1 - fy) + v[y0 * w + x0 + 1] * fx * (1 - fy)
    + v[(y0 + 1) * w + x0] * (1 - fx) * fy + v[(y0 + 1) * w + x0 + 1] * fx * fy;
};

// Il contrasto dei bordi, misurato in RAPPORTO al contrasto medio lì intorno: così il bordo debole di un foglio
// chiaro su un tavolo chiaro conta quanto una riga di testo nera, che sul foglio è fortissima ma sta in mezzo ad
// altre righe. Poi si assottigliano i bordi (si tiene solo la cima del gradiente) perché la retta sia netta.
export const contrastoBordi = (grigi, w, h) => {
  const { salto, gx, gy } = sobel(sfoca3(grigi, w, h), w, h);
  const media = mediaIntorno(salto, w, h, INTORNO);
  const rapporto = new Float32Array(w * h);
  for (let i = 0; i < w * h; i += 1) rapporto[i] = salto[i] < RUMORE ? 0 : salto[i] / (media[i] + 2);
  const forza = new Float32Array(w * h);
  for (let y = 1; y < h - 1; y += 1) {
    for (let x = 1; x < w - 1; x += 1) {
      const i = y * w + x;
      if (!rapporto[i]) continue;
      const m = Math.hypot(gx[i], gy[i]);
      const nx = gx[i] / m; const ny = gy[i] / m;
      if (rapporto[i] >= campiona(rapporto, w, h, x + nx, y + ny) && rapporto[i] >= campiona(rapporto, w, h, x - nx, y - ny)) {
        forza[i] = rapporto[i];
      }
    }
  }
  return { forza, gx, gy };
};

// La soglia che tiene i pixel col contrasto più forte (una frazione di tutta la foto)
export const sogliaContrasto = (forza, w, h, parte = PARTE_BORDI) => {
  const ist = new Array(129).fill(0); // mezzo punto di rapporto per casella
  for (let i = 0; i < w * h; i += 1) if (forza[i]) ist[Math.min(128, Math.round(forza[i] * 2))] += 1;
  let restano = Math.round(w * h * parte);
  for (let v = 128; v > CONTRASTO_MINIMO * 2; v -= 1) {
    restano -= ist[v];
    if (restano <= 0) return v / 2;
  }
  return CONTRASTO_MINIMO;
};

// Trasformata di Hough con l'aiuto del gradiente: ogni pixel di bordo vota solo per le rette messe come lui.
// Due tabelle: le rette «in piedi» (quasi verticali) e quelle «sdraiate» (quasi orizzontali); le coordinate
// partono dal centro della foto, così il segno di rho dice subito da che parte sta la retta.
export const votaRette = (forza, gx, gy, w, h, soglia) => {
  const rhoMax = Math.ceil(Math.hypot(w, h) / 2) + 1;
  const nRho = rhoMax * 2 + 1;
  const piedi = new Float32Array(CASELLE * nRho);
  const sdraiate = new Float32Array(CASELLE * nRho);
  const cx = (w - 1) / 2; const cy = (h - 1) / 2;
  const cos = new Float32Array(180); const sin = new Float32Array(180);
  for (let t = 0; t < 180; t += 1) { cos[t] = Math.cos(t * GRADI); sin[t] = Math.sin(t * GRADI); }
  for (let y = 1; y < h - 1; y += 1) {
    for (let x = 1; x < w - 1; x += 1) {
      const i = y * w + x;
      if (forza[i] < soglia) continue;
      const dx = x - cx; const dy = y - cy;
      const base = ((Math.round(Math.atan2(gy[i], gx[i]) / GRADI) % 180) + 180) % 180;
      for (let d = -SETTORE; d <= SETTORE; d += 1) {
        const t = (base + d + 180) % 180;
        let r = dx * cos[t] + dy * sin[t];
        let casella; let tabella;
        if (t < 45) { tabella = piedi; casella = t + 45; } else if (t < 135) { tabella = sdraiate; casella = t - 45; } else { tabella = piedi; casella = t - 135; r = -r; }
        tabella[casella * nRho + Math.round(r) + rhoMax] += 1;
      }
    }
  }
  return { piedi, sdraiate, nRho, rhoMax, cx, cy };
};

// Le rette candidate per un lato: `verso` −1 = sinistro/alto, +1 = destro/basso. Conta la casella più votata
// (sommare le vicine farebbe vincere le righe di testo, che sono a coppie a due passi l'una dall'altra); la
// finestrella intorno serve solo a raffinare l'angolo e la distanza. Le cime si tengono lontane fra loro.
export const retteDelLato = (tabella, { nRho, rhoMax }, inPiedi, verso, w, h, quante = QUANTE_RETTE) => {
  const mezzo = (inPiedi ? w : h) / 2;
  const minimo = FUORI_CENTRO * (inPiedi ? w : h);
  const votiMinimi = VOTI_MINIMI * (inPiedi ? h : w);
  const cime = [];
  for (let casella = 0; casella < CASELLE; casella += 1) {
    const theta = (inPiedi ? casella - 45 : casella + 45) * GRADI;
    const proiezione = inPiedi ? Math.cos(theta) : Math.sin(theta); // sempre ≥ 0,70
    for (let r = -rhoMax; r <= rhoMax; r += 1) {
      const dove = r / proiezione; // dove la retta taglia l'asse centrale della foto
      const buona = verso < 0 ? dove <= -minimo && dove >= -mezzo - 2 : dove >= minimo && dove <= mezzo + 2;
      if (!buona) continue;
      const voti = tabella[casella * nRho + r + rhoMax];
      if (voti < votiMinimi) continue;
      let peso = 0; let sc = 0; let sr = 0;
      for (let dc = -1; dc <= 1; dc += 1) {
        for (let dr = -1; dr <= 1; dr += 1) {
          const c2 = casella + dc; const r2 = r + dr;
          if (c2 < 0 || c2 >= CASELLE || r2 < -rhoMax || r2 > rhoMax) continue;
          const v = tabella[c2 * nRho + r2 + rhoMax];
          peso += v; sc += v * c2; sr += v * r2;
        }
      }
      cime.push({ casella, r, voti, cm: sc / peso, rm: sr / peso });
    }
  }
  cime.sort((a, b) => b.voti - a.voti);
  const scelte = [];
  for (const c of cime) {
    if (scelte.some((s) => Math.abs(s.casella - c.casella) <= 2 && Math.abs(s.r - c.r) <= 4)) continue;
    scelte.push(c);
    if (scelte.length >= quante) break;
  }
  return scelte.map((c) => ({ theta: (inPiedi ? c.cm - 45 : c.cm + 45) * GRADI, rho: c.rm, voti: c.voti }));
};

// Dove si incrociano due rette (coordinate dal centro); null se sono quasi parallele
export const incrocio = (a, b) => {
  const c1 = Math.cos(a.theta); const s1 = Math.sin(a.theta);
  const c2 = Math.cos(b.theta); const s2 = Math.sin(b.theta);
  const det = c1 * s2 - s1 * c2;
  if (Math.abs(det) < 0.25) return null; // meno di 15° fra i due lati: non è un foglio
  return [(a.rho * s2 - b.rho * s1) / det, (c1 * b.rho - c2 * a.rho) / det];
};

// C'è un bordo vero in questo punto, messo come la retta? Si guarda a cavallo della retta (±1,5 px)
const sulBordo = (bordi, w, h, x, y, nx, ny, soglia) => {
  const { forza, gx, gy } = bordi;
  for (let d = -1.5; d <= 1.5; d += 0.75) {
    const xx = Math.round(x + nx * d); const yy = Math.round(y + ny * d);
    if (xx < 1 || yy < 1 || xx >= w - 1 || yy >= h - 1) continue;
    const i = yy * w + xx;
    if (forza[i] < soglia) continue;
    if (Math.abs(gx[i] * nx + gy[i] * ny) > 0.9 * Math.hypot(gx[i], gy[i])) return 1;
  }
  return 0;
};

// Quanto quel lato è davvero il bordo del foglio. `dentro`: quanta parte del segmento fra i due angoli poggia su
// un bordo. `fuori`: quanto quel bordo tira dritto oltre gli angoli — l'ombra buttata di traverso attraversa
// tutta la foto e ha `dentro` alto come il foglio, ma il foglio finisce agli angoli e l'ombra no.
export const appoggioLato = (bordi, w, h, p, q, soglia) => {
  const [x1, y1] = p; const [x2, y2] = q;
  const lung = Math.hypot(x2 - x1, y2 - y1);
  if (lung < 8) return 0;
  const ux = (x2 - x1) / lung; const uy = (y2 - y1) / lung;
  const nx = -uy; const ny = ux;
  const prova = (t) => sulBordo(bordi, w, h, x1 + ux * t, y1 + uy * t, nx, ny, soglia);
  let dentro = 0;
  for (let k = 0; k < CAMPIONI; k += 1) dentro += prova(((k + 0.5) / CAMPIONI) * lung);
  const coda = Math.max(6, lung * CODA);
  let fuori = 0; let quanti = 0;
  for (let k = 1; k <= 8; k += 1) {
    for (const t of [-(k / 8) * coda, lung + (k / 8) * coda]) {
      const x = x1 + ux * t; const y = y1 + uy * t;
      if (x < 1 || y < 1 || x >= w - 1 || y >= h - 1) continue; // oltre il bordo della foto non si sa
      quanti += 1;
      fuori += prova(t);
    }
  }
  return dentro / CAMPIONI - 0.7 * (quanti ? fuori / quanti : 0);
};

const area = (a) => {
  let s = 0;
  for (let i = 0; i < 4; i += 1) { const [x1, y1] = a[i]; const [x2, y2] = a[(i + 1) % 4]; s += x1 * y2 - x2 * y1; }
  return s / 2;
};

// Convesso e girato in senso orario (alto-sx, alto-dx, basso-dx, basso-sx)
const convesso = (a) => {
  for (let i = 0; i < 4; i += 1) {
    const [x1, y1] = a[i]; const [x2, y2] = a[(i + 1) % 4]; const [x3, y3] = a[(i + 2) % 4];
    if ((x2 - x1) * (y3 - y2) - (y2 - y1) * (x3 - x2) <= 0) return false;
  }
  return true;
};

// Angoli [alto-sx, alto-dx, basso-dx, basso-sx] dai bordi, o null se non siamo sicuri
export const trovaAngoliBordi = (grigi, w, h) => {
  if (w < 24 || h < 24) return null;
  const bordi = contrastoBordi(grigi, w, h);
  const soglia = sogliaContrasto(bordi.forza, w, h);
  const tabelle = votaRette(bordi.forza, bordi.gx, bordi.gy, w, h, soglia);
  const lati = {
    sinistro: retteDelLato(tabelle.piedi, tabelle, true, -1, w, h),
    destro: retteDelLato(tabelle.piedi, tabelle, true, 1, w, h),
    alto: retteDelLato(tabelle.sdraiate, tabelle, false, -1, w, h),
    basso: retteDelLato(tabelle.sdraiate, tabelle, false, 1, w, h),
  };
  if (Object.values(lati).some((l) => !l.length)) return null;
  const { cx, cy } = tabelle;
  const daCentro = ([x, y]) => [x + cx, y + cy];
  const memoria = new Map();
  const appoggio = (chiave, p, q) => {
    if (!memoria.has(chiave)) memoria.set(chiave, appoggioLato(bordi, w, h, p, q, soglia));
    return memoria.get(chiave);
  };
  let migliore = null;
  for (let a = 0; a < lati.sinistro.length; a += 1) {
    for (let b = 0; b < lati.destro.length; b += 1) {
      for (let c = 0; c < lati.alto.length; c += 1) {
        for (let d = 0; d < lati.basso.length; d += 1) {
          const tl = incrocio(lati.sinistro[a], lati.alto[c]);
          const tr = incrocio(lati.destro[b], lati.alto[c]);
          const br = incrocio(lati.destro[b], lati.basso[d]);
          const bl = incrocio(lati.sinistro[a], lati.basso[d]);
          if (!tl || !tr || !br || !bl) continue;
          const angoli = [tl, tr, br, bl].map(daCentro);
          if (angoli.some(([x, y]) => x < -0.08 * w || x > 1.08 * w || y < -0.08 * h || y > 1.08 * h)) continue;
          if (!convesso(angoli) || area(angoli) < 0.08 * w * h) continue;
          const punteggio = (
            appoggio(`a${c}-${a}-${b}`, angoli[0], angoli[1]) + appoggio(`d${b}-${c}-${d}`, angoli[1], angoli[2])
            + appoggio(`b${d}-${a}-${b}`, angoli[3], angoli[2]) + appoggio(`s${a}-${c}-${d}`, angoli[0], angoli[3])
          ) / 4;
          if (!migliore || punteggio > migliore.punteggio) migliore = { punteggio, angoli };
        }
      }
    }
  }
  if (!migliore || migliore.punteggio < APPOGGIO_MINIMO) return null;
  return migliore.angoli.map(([x, y]) => [Math.max(0, Math.min(w - 1, x)), Math.max(0, Math.min(h - 1, y))]);
};

// Angoli [alto-sx, alto-dx, basso-dx, basso-sx] del foglio in coordinate dell'immagine analizzata, o null:
// prima i bordi, poi (se non bastano) la zona chiara più grande
export const trovaAngoli = (grigi, w, h) => trovaAngoliBordi(grigi, w, h) || trovaAngoliChiaro(grigi, w, h);

// Omografia che porta i 4 punti "da" nei 4 punti "a": matrice 3×3 (h33 = 1), sistema 8×8 con Gauss
export const omografia = (da, a) => {
  const A = [];
  const b = [];
  for (let i = 0; i < 4; i += 1) {
    const [x, y] = da[i]; const [u, v] = a[i];
    A.push([x, y, 1, 0, 0, 0, -u * x, -u * y]); b.push(u);
    A.push([0, 0, 0, x, y, 1, -v * x, -v * y]); b.push(v);
  }
  const n = 8;
  for (let c = 0; c < n; c += 1) {
    let pivot = c;
    for (let r = c + 1; r < n; r += 1) if (Math.abs(A[r][c]) > Math.abs(A[pivot][c])) pivot = r;
    [A[c], A[pivot]] = [A[pivot], A[c]];
    [b[c], b[pivot]] = [b[pivot], b[c]];
    if (Math.abs(A[c][c]) < 1e-12) return null;
    for (let r = 0; r < n; r += 1) {
      if (r === c) continue;
      const f = A[r][c] / A[c][c];
      for (let k = c; k < n; k += 1) A[r][k] -= f * A[c][k];
      b[r] -= f * b[c];
    }
  }
  const h = b.map((v, i) => v / A[i][i]);
  return [[h[0], h[1], h[2]], [h[3], h[4], h[5]], [h[6], h[7], 1]];
};

export const applica = (H, [x, y]) => {
  const z = H[2][0] * x + H[2][1] * y + H[2][2];
  return [(H[0][0] * x + H[0][1] * y + H[0][2]) / z, (H[1][0] * x + H[1][1] * y + H[1][2]) / z];
};

const dist = (p, q) => Math.hypot(p[0] - q[0], p[1] - q[1]);

// Misure del foglio raddrizzato (dal quadrilatero), con il lato lungo al massimo LATO_MASSIMO
export const misureUscita = (angoli) => {
  const [tl, tr, br, bl] = angoli;
  let w = Math.max(dist(tl, tr), dist(bl, br));
  let h = Math.max(dist(tl, bl), dist(tr, br));
  const scala = Math.min(1, LATO_MASSIMO / Math.max(w, h));
  w = Math.max(1, Math.round(w * scala)); h = Math.max(1, Math.round(h * scala));
  return [w, h];
};

// Raddrizza: per ogni pixel d'uscita si prende il punto corrispondente nella foto (mappa inversa, bilineare)
export const raddrizzaPixel = (src, sw, sh, angoli) => {
  const [w, h] = misureUscita(angoli);
  const H = omografia([[0, 0], [w - 1, 0], [w - 1, h - 1], [0, h - 1]], angoli);
  if (!H) return null;
  const out = new Uint8ClampedArray(w * h * 4);
  for (let y = 0; y < h; y += 1) {
    for (let x = 0; x < w; x += 1) {
      const [sx, sy] = applica(H, [x, y]);
      const x0 = Math.max(0, Math.min(sw - 2, Math.floor(sx))); const y0 = Math.max(0, Math.min(sh - 2, Math.floor(sy)));
      const fx = Math.max(0, Math.min(1, sx - x0)); const fy = Math.max(0, Math.min(1, sy - y0));
      const o = (y * w + x) * 4;
      for (let c = 0; c < 3; c += 1) {
        const p00 = src[(y0 * sw + x0) * 4 + c]; const p10 = src[(y0 * sw + x0 + 1) * 4 + c];
        const p01 = src[((y0 + 1) * sw + x0) * 4 + c]; const p11 = src[((y0 + 1) * sw + x0 + 1) * 4 + c];
        out[o + c] = p00 * (1 - fx) * (1 - fy) + p10 * fx * (1 - fy) + p01 * (1 - fx) * fy + p11 * fx * fy;
      }
      out[o + 3] = 255;
    }
  }
  return { dati: out, w, h };
};

// ---------------------------------------------------------------- nel browser (canvas)

// Certe fotocamere Android consegnano i file senza tipo MIME (`file.type` vuoto): prima sparivano in silenzio,
// adesso si guarda l'estensione del nome.
const ESTENSIONI_FOTO = /\.(jpe?g|png|heic|heif|webp|gif|bmp|tiff?)$/i;
export const eFoto = (file) => (file?.type ? file.type.startsWith('image/') : ESTENSIONI_FOTO.test(file?.name || ''));

// Gli HEIC di iPhone: Chrome e Firefox non li sanno disegnare, quindi non si possono raddrizzare qui (li converte
// il server). Serve per dirlo al cliente invece di far finta di niente.
export const eHeic = (file) => /heic|heif/i.test(file?.type || '') || /\.(heic|heif)$/i.test(file?.name || '');

export const caricaImmagine = (file) => new Promise((ok, ko) => {
  const url = URL.createObjectURL(file);
  const img = new Image();
  img.onload = () => ok({ img, url });
  img.onerror = () => { URL.revokeObjectURL(url); ko(new Error('Foto non leggibile')); };
  img.src = url;
});

// Il rettangolo pieno, quello che si propone quando i bordi non si trovano: non tocca il bordo della foto, se no
// due maniglie finiscono a cavallo dello schermo e al telefono non si prendono più
export const rettangoloPieno = (m = MARGINE_PIENO) => [[m, m], [1 - m, m], [1 - m, 1 - m], [m, 1 - m]];

// Un angolo resta sempre dentro l'area, con un margine che lo lascia afferrabile anche su uno schermo da 390 px
export const dentroArea = ([x, y], m = MARGINE) => [Math.max(m, Math.min(1 - m, x)), Math.max(m, Math.min(1 - m, y))];

// Gli angoli proposti, in frazioni della foto (0-1). `trovato` è falso quando i bordi non si vedono: in quel caso
// non si inventa niente, si propone il rettangolo pieno e al cliente lo si dice.
export const angoliProposti = (img) => {
  const scala = LATO_ANALISI / Math.max(img.naturalWidth, img.naturalHeight);
  const w = Math.max(8, Math.round(img.naturalWidth * scala)); const h = Math.max(8, Math.round(img.naturalHeight * scala));
  let trovati = null;
  try {
    const c = document.createElement('canvas');
    c.width = w; c.height = h;
    const ctx = c.getContext('2d');
    ctx.drawImage(img, 0, 0, w, h);
    trovati = trovaAngoli(inGrigi(ctx.getImageData(0, 0, w, h).data, w, h), w, h);
  } catch { /* canvas «sporco», memoria finita: si propone il rettangolo pieno e lo si dice */ }
  if (!trovati) return { angoli: rettangoloPieno(), trovato: false };
  return { angoli: trovati.map(([x, y]) => dentroArea([x / (w - 1), y / (h - 1)])), trovato: true };
};

// La foto raddrizzata come File JPEG
export const fotoRaddrizzata = async (img, angoliFrazione, nome = 'documento.jpg') => {
  const sw = img.naturalWidth; const sh = img.naturalHeight;
  const scala = Math.min(1, (LATO_MASSIMO * 1.5) / Math.max(sw, sh));
  const w = Math.round(sw * scala); const h = Math.round(sh * scala);
  const c = document.createElement('canvas');
  c.width = w; c.height = h;
  const ctx = c.getContext('2d');
  ctx.drawImage(img, 0, 0, w, h);
  const angoli = angoliFrazione.map(([x, y]) => [x * (w - 1), y * (h - 1)]);
  const r = raddrizzaPixel(ctx.getImageData(0, 0, w, h).data, w, h, angoli);
  if (!r) throw new Error('Angoli non validi');
  const uscita = document.createElement('canvas');
  uscita.width = r.w; uscita.height = r.h;
  uscita.getContext('2d').putImageData(new ImageData(r.dati, r.w, r.h), 0, 0);
  const blob = await new Promise((ok) => uscita.toBlob(ok, 'image/jpeg', 0.86));
  const file = new File([blob], nome.replace(/\.[^.]+$/, '') + '-raddrizzata.jpg', { type: 'image/jpeg' });
  file.raddrizzata = true;
  return file;
};
